import React, { useState, useEffect } from "react";
import { Button, Table, Modal, Form, Select, message, Space, Spin, Tag, Input } from "antd";
import { DeleteOutlined, PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import { Country, State, City } from "country-state-city";
import axios from "axios";

interface LocationData {
  _id?: string;
  city: string;
  state: string;
  country: string;
  createdAt?: string;
  updatedAt?: string;
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

const LocationComponent: React.FC = () => {
  const [form] = Form.useForm();
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [selectedStateCode, setSelectedStateCode] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);

  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem("userDetails");
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || "";
  };
  const userRole = getUserRole();
  const isAdmin = userRole === "admin";

  // Initialize countries
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    // Priority Codes
    const priorityIsoCodes = ["IN", "AE"];
    // Sort logic: India -> UAE -> Rest
    const priorityCountries = allCountries.filter((c) =>
      priorityIsoCodes.includes(c.isoCode)
    );
    const otherCountries = allCountries.filter(
      (c) => !priorityIsoCodes.includes(c.isoCode)
    );
    setCountries([...priorityCountries, ...otherCountries]);
  }, []);

  // Fetch locations
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://service.ireedindia.com/v1/get-location"
      );
      const data = Array.isArray(response.data) ? response.data : response.data.data || [];
      setLocations(data);
      setFilteredLocations(data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      message.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredLocations(locations);
    } else {
      const filtered = locations.filter((location) =>
        location.city.toLowerCase().includes(value.toLowerCase()) ||
        location.state.toLowerCase().includes(value.toLowerCase()) ||
        location.country.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredLocations(filtered);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchText("");
    setFilteredLocations(locations);
  };

  // Handle country change
  const handleCountryChange = (countryCode: string) => {
    setSelectedCountryCode(countryCode);
    setSelectedStateCode("");
    const countryStates = State.getStatesOfCountry(countryCode);
    setStates(countryStates);
    setCities([]);
    form.setFieldsValue({ state: undefined, city: undefined });
  };

  // Handle state change
  const handleStateChange = (stateCode: string) => {
    setSelectedStateCode(stateCode);
    const stateCities = City.getCitiesOfState(selectedCountryCode, stateCode);
    
    // Apply city name modifications (e.g., Gurgaon -> Gurugram)
    const modifiedCities = stateCities.map((city: any) => {
      if (city.name === "Gurgaon") {
        return { ...city, name: "Gurugram" };
      }
      return city;
    });
    
    setCities(modifiedCities);
    form.setFieldsValue({ city: undefined });
  };

  // Handle submit - Create or Edit
  const onFinish = async (values: any) => {
    try {
      const countryName =
        countries.find((c) => c.isoCode === values.country)?.name || values.country;
      const stateName =
        states.find((s) => s.isoCode === values.state)?.name || values.state;

      const payload: any = {
        city: values.city,
        state: stateName,
        country: countryName,
      };

      if (editingLocationId) {
        // EDIT: Patch request
        const response = await axios.patch(
          `https://service.ireedindia.com/v1/edit-location/${editingLocationId}`,
          payload
        );
        message.success("Location updated successfully");
      } else {
        // CREATE: POST request
        const response = await axios.post(
          "https://service.ireedindia.com/v1/create-location",
          payload
        );
        message.success("Location created successfully");
      }

      form.resetFields();
      setSelectedCountryCode("");
      setSelectedStateCode("");
      setStates([]);
      setCities([]);
      setEditingLocationId(null);
      setIsModalVisible(false);
      fetchLocations();
    } catch (error) {
      console.error("Error saving location:", error);
      message.error("Failed to save location");
    }
  };

  // Handle delete
  const handleDelete = async (locationId: string) => {
    try {
      await axios.delete(
        `https://service.ireedindia.com/v1/delete-location/${locationId}`
      );
      message.success("Location deleted successfully");
      fetchLocations();
    } catch (error) {
      console.error("Error deleting location:", error);
      message.error("Failed to delete location");
    }
  };

  // Handle edit
  const handleEdit = (record: LocationData) => {
    const countryCode = countries.find((c) => c.name === record.country)?.isoCode;
    let stateCode: string | undefined;

    if (countryCode) {
      setSelectedCountryCode(countryCode);
      const countryStates = State.getStatesOfCountry(countryCode);
      setStates(countryStates);

      // Find state code from the fetched states
      stateCode = countryStates.find((s) => s.name === record.state)?.isoCode;

      if (stateCode) {
        setSelectedStateCode(stateCode);
        const stateCities = City.getCitiesOfState(countryCode, stateCode);
        const modifiedCities = stateCities.map((city: any) => {
          if (city.name === "Gurgaon") {
            return { ...city, name: "Gurugram" };
          }
          return city;
        });
        setCities(modifiedCities);
      }
    }

    form.setFieldsValue({
      country: countryCode,
      state: stateCode,
      city: record.city,
    });

    setEditingLocationId(record._id ?? null);
    setIsModalVisible(true);
  };

  const handleDeleteClick = (id: string) => {
    Modal.confirm({
      title: "Delete Location",
      content: "Are you sure you want to delete this location?",
      okText: "Yes",
      cancelText: "No",
      onOk() {
        handleDelete(id);
      },
    });
  };

  const columns = [
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    ...(isAdmin
      ? [
          {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            width: "15%",
            render: (createdAt: string) => {
              if (!createdAt) return "N/A";
              const date = new Date(createdAt);
              return (
                <div style={{ fontSize: "12px", color: "#4b5563" }}>
                  <div>{date.toLocaleDateString()}</div>
                  <div style={{ color: "#9ca3af" }}>
                    {date.toLocaleTimeString()}
                  </div>
                </div>
              );
            },
          },
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            key: "updatedAt",
            width: "15%",
            render: (updatedAt: string) => {
              if (!updatedAt) return "N/A";
              const date = new Date(updatedAt);
              return (
                <div style={{ fontSize: "12px", color: "#4b5563" }}>
                  <div>{date.toLocaleDateString()}</div>
                  <div style={{ color: "#9ca3af" }}>
                    {date.toLocaleTimeString()}
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: LocationData) =>
        isAdmin && (
          <Space size="middle">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteClick(record._id ?? "")}
            />
          </Space>
        ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          form.resetFields();
          setEditingLocationId(null);
          setSelectedCountryCode("");
          setSelectedStateCode("");
          setStates([]);
          setCities([]);
          setIsModalVisible(true);
        }}>
          Add New Location
        </Button>
        
        <Input
          placeholder="Search by country, state, or city..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          style={{ width: '280px' }}
        />

        {searchText && (
          <Button 
            type="default" 
            size="small" 
            onClick={handleClearSearch}
          >
            Clear Search
          </Button>
        )}
      </div>

      {searchText && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: '#f0f7ff', borderRadius: 4, display: 'flex', gap: 12, alignItems: 'center' }}>
          <Tag color="blue">🔍 {searchText}</Tag>
          <span style={{ color: '#666' }}>Found <strong>{filteredLocations.length}</strong> location{filteredLocations.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={filteredLocations}
          rowKey="_id"
          scroll={{ x: "max-content" }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Spin>

      <Modal
        title={editingLocationId ? "Edit Location" : "Add New Location"}
        visible={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
          setSelectedCountryCode("");
          setSelectedStateCode("");
          setStates([]);
          setCities([]);
          setEditingLocationId(null);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Country"
            name="country"
            rules={[
              { required: true, message: "Please select a country" },
            ]}
          >
            <Select
              placeholder="Select Country"
              onChange={handleCountryChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={countries.map((country) => ({
                label: country.name,
                value: country.isoCode,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="State"
            name="state"
            rules={[
              { required: true, message: "Please select a state" },
            ]}
          >
            <Select
              placeholder="Select State"
              onChange={handleStateChange}
              disabled={!selectedCountryCode}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={states.map((state) => ({
                label: state.name,
                value: state.isoCode,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="City"
            name="city"
            rules={[
              { required: true, message: "Please select a city" },
            ]}
          >
            <Select
              placeholder="Select City"
              disabled={!selectedStateCode}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={cities.map((city) => ({
                label: city.name,
                value: city.name,
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingLocationId ? "Update Location" : "Create Location"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LocationComponent;
